import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import useAlerts from '@/hooks/useAlerts';
import useToasts from '@/hooks/useToasts';
import Progress from '@/pages/CoverImagesGenerator/components/Progress';
import { MediaResource } from '@/types/resources';
import { api } from '@/utils/api';
import {
  CoverImagesImageStatus,
  CoverImagesProjectStatus,
} from '@/utils/enums';

const Box = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  margin-bottom: 48px;
`;

const StyledButton = styled(Button)`
  background-color: transparent;
`;

interface Props {
  setSelectedImages: React.Dispatch<React.SetStateAction<number[]>>;
  selectedImages: number[];
  status: CoverImagesImageStatus;
  sentImagesCount?: number;
  images?: MediaResource<
    {},
    {
      status: CoverImagesImageStatus;
      isMain?: boolean;
    }
  >[];
}

const ArticleButtons: FC<Props> = ({
  selectedImages,
  sentImagesCount,
  setSelectedImages,
  status,
  images,
}) => {
  const [isPosting, setIsPosting] = useState(false);

  const [isConfirmig, setIsConfirming] = useState(false);

  const [isSending, setIsSending] = useState(false);

  const [progressCounter, setProgressCounter] = useState(0);

  const { id } = useParams();

  const alerts = useAlerts();

  const toasts = useToasts();

  const queryClient = useQueryClient();

  const filteredImages = images?.filter((image) =>
    selectedImages.includes(image.id),
  );

  const hasMainImage = filteredImages?.find((image) => image.meta?.isMain);

  const isInitialSend = !sentImagesCount;

  const onMoveToFixing = async () => {
    const toastId = toasts.loading();

    setIsPosting(true);
    try {
      await api.put(`/v1/cover-images/cover-images-projects/${id}`, {
        status: CoverImagesProjectStatus.REVIEW,
        images: selectedImages.join(','),
      });

      await queryClient.invalidateQueries(['cover_images_project', { id }], {
        exact: true,
      });

      toasts.success(toastId);
      setSelectedImages([]);
    } catch (_) {
      toasts.error(toastId);
    }

    setIsPosting(false);
  };

  const onSendToBp = async () => {
    const confirmed = await alerts.confirm(
      'Do you want to create experiment for this post?',
    );

    alerts.close();

    const toastId = toasts.loading();
    setIsConfirming(true);
    setIsSending(true);

    try {
      let countForExp = 0;
      let counter = sentImagesCount ? 1 + sentImagesCount : 2;
      for (let image of selectedImages) {
        setProgressCounter((prev) => prev + 1);

        await api.post(`/v1/cover-images/send-to-bp`, {
          post_id: id,
          init_experiment: countForExp === 0 && confirmed,
          image_id: image,
          image_number: filteredImages?.find(
            (filteredImage) => filteredImage.id === image,
          )?.meta?.isMain
            ? 0
            : counter,
        });

        if (
          !filteredImages?.find((filteredImage) => filteredImage.id === image)
            ?.meta?.isMain
        ) {
          counter++;
        }

        countForExp++;
      }

      if (!sentImagesCount) {
        await api.post(`/v1/cover-images/send-slack-notif/${id}`, {
          status: CoverImagesProjectStatus.SENT,
          ...(!!filteredImages?.find((image) => image.meta?.isMain)?.id && {
            image_id: filteredImages?.find((image) => image.meta?.isMain)?.id,
          }),
        });
      }

      queryClient.invalidateQueries(['cover_images_project', { id }], {
        exact: true,
      });

      setSelectedImages([]);
      toasts.success(toastId);
    } catch (_) {
      toasts.error(toastId);
    }
    setIsConfirming(false);
    setIsSending(false);

    setProgressCounter(0);
  };

  const onApprove = async () => {
    const toastId = toasts.loading();

    setIsConfirming(true);
    try {
      await api.put(`/v1/cover-images/cover-images-projects/${id}`, {
        status: CoverImagesProjectStatus.OK,
        images: selectedImages.join(','),
      });

      queryClient.invalidateQueries(['cover_images_project', { id }], {
        exact: true,
      });

      setSelectedImages([]);
      toasts.success(toastId);
    } catch (_) {
      toasts.error(toastId);
    }
    setIsConfirming(false);
  };

  const onImagesDelete = async () => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure you want to do delete selected images?',
      );
      if (!confirmed) return;

      await api.post(`/v1/cover-images/delete-cover-images`, {
        project_id: id,
        images_ids: selectedImages,
      });

      await queryClient.invalidateQueries(['cover_images_project', { id }], {
        exact: true,
      });

      setSelectedImages([]);
      alerts.success('Selected images been successfully deleted');
    } catch (_) {
      alerts.error();
    }
  };

  const onMoveToDone = async () => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure you want to move this article to done?',
      );

      if (!confirmed) return;

      await api.post(`/v1/cover-images/end-article/${id}`);

      await queryClient.invalidateQueries(['cover_images_projects']);

      alerts.success('Article has been successfully moved to done');
    } catch (_) {
      alerts.error();
    }
  };

  return (
    <>
      <Box>
        {status === CoverImagesImageStatus.APPROVED && (
          <StyledButton
            disabled={isConfirmig || isPosting}
            onClick={onMoveToDone}
            color="secondary"
          >
            Move Article to Done
          </StyledButton>
        )}
        {status === CoverImagesImageStatus.APPROVED && (
          <StyledButton
            disabled={
              isConfirmig ||
              isPosting ||
              (!hasMainImage && !!isInitialSend) ||
              !selectedImages.length
            }
            onClick={onSendToBp}
            color="secondary"
          >
            Send Selected to BP
          </StyledButton>
        )}
        {status === CoverImagesImageStatus.PENDING && (
          <StyledButton
            onClick={onApprove}
            disabled={isConfirmig || isPosting || !selectedImages.length}
            loading={isConfirmig}
          >
            Approve Selected
          </StyledButton>
        )}
        {status !== CoverImagesImageStatus.FIXING &&
          status !== CoverImagesImageStatus.SENT && (
            <StyledButton
              color="secondary"
              onClick={onMoveToFixing}
              disabled={isConfirmig || isPosting || !selectedImages.length}
              loading={isPosting}
            >
              Move Selected to fixing
            </StyledButton>
          )}
        {status !== CoverImagesImageStatus.SENT && (
          <Button
            disabled={isConfirmig || isPosting || !selectedImages.length}
            onClick={onImagesDelete}
            color="danger"
          >
            Delete Selected
          </Button>
        )}
      </Box>
      <AnimatePresence>
        {!!isSending && (
          <Progress
            text="Sending images..."
            progressPercent={(progressCounter / selectedImages.length) * 100}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ArticleButtons;
